import api from "@/api/gql.js"
import { gql } from "@/api/utils.js"
import dayjs from "dayjs"

import {
	getTotalInsuredAmount,
	getBinderNumber,
	getAssured,
	getAddress,
	getCity,
	getPostalCode,
	getCountry,
	getPolicyFromTransactionObj,
	formatDate,
	getCommissionRatio,
	getCommissionAmount
} from "./helpers"

const header = [["Premium Bordereaux Report"]]

const subheader = [
	"Insurer",
	"Binder Number",
	"Underwriting Year From",
	"Underwriting Year To",
	"A/C Code",
	"Type of Insurance",
	"Certificate Number",
	"Debit Note Number",
	"Debit Note Date",
	"Assured Name",
	"Address",
	"City",
	"Country/Postal",
	"Policy Period From",
	"Policy Period To",
	"Currency",
	"Insured Amount",
	"Gross Premium",
	"Agency Commission %",
	"Agency Commission Amount",
	"Net Payable"
]

/****************** Define Rows ******************/
const filterDates = filterPayload => {
	const dateFrom = filterPayload?.issueDate?.after
	const dateTo = filterPayload?.issueDate?.before
	return [["For Period:", `${dateFrom} to ${dateTo}`], []]
}
const rows = fetchedData => {
	let output = []

	let binderGroup = {}

	fetchedData?.reverse()?.map(transaction => {
		const policy = getPolicyFromTransactionObj(transaction)
		// create an object and group all policies into seperate arrays by binderNumber
		const binderNumber = getBinderNumber(policy)
		if (!binderNumber) return
		if (!Object.keys(binderGroup).includes(binderNumber)) {
			binderGroup[binderNumber] = []
		}
		binderGroup[binderNumber].push(transaction)
	})

	// iterate over values in binderGroup and push to output
	let binderGroupValues = Object.values(binderGroup)
	binderGroupValues.map(group => {
		output.push(subheader)
		group.map(transaction => {
			const policy = getPolicyFromTransactionObj(transaction)
			const assured = getAssured(policy)

			output.push([
				"",
				getBinderNumber(policy),
				getUnderwritingYearFrom(policy),
				getUnderwritingYearTo(policy),
				getAcCode(policy),
				getInsuranceType(policy),
				policy?.issuerNumber,
				getTransactionNumber(policy),
				formatDate(transaction?.dateTime),
				assured?.name,
				getAddress(assured),
				getCity(assured),
				`${getCountry(assured)}/${getPostalCode(assured)}`,
				formatDate(policy?.startDate),
				formatDate(policy?.endDate),
				transaction?.currencyCode,
				getTotalInsuredAmount(policy),
				getGrossPremium(transaction),
				getCommissionRatio("coverholder", policy) * 100,
				getCommissionAmount("coverholder", transaction?.amount, policy),
				getNetPayable(transaction)
			])
		})
		output.push(
			[],
			...getCurrencyTotal("currency", group),
			...getCurrencyTotal("binder", group), // binder grand total is same as currency total is the same
			[],
			[]
		)
	})

	return output
}

/****************** Helper Functions ******************/
const getUnderwritingYearFrom = policy => {
	const date = policy?.associatedContracts?.[0]?.contract?.startDate
	return formatDate(date)
}
const getUnderwritingYearTo = policy => {
	const date = policy?.associatedContracts?.[0]?.contract?.endDate
	return formatDate(date)
}
const getAcCode = policy => {
	return policy?.associatedContracts?.[0]?.contract?.facts?.find(
		fact => fact.type === "acCode"
	)?.value?.stringValue
}
const getInsuranceType = policy => {
	let productType = policy?.product?.productId?.type
	return productType === "jewellers_block"
		? "JB Jewellers Block"
		: productType === "jewellers_tour"
		? "TO Jewellers Tour"
		: productType === "sending"
		? "SI Sending"
		: productType === "commercial_block"
		? "CB Commercial Block"
		: productType === "employees_compensation"
		? "EC Employees Compensation"
		: productType === "public_liability"
		? "PL Public Liability"
		: productType === "personal_jewellery"
		? "PJ Personal Jewellery"
		: productType === "jewellers_exhibition"
		? "EX Jewellers Exhibition"
		: null
}
const getTransactionNumber = policy => {
	return policy?.transactions?.[0]?.transactionNumber
}
const getGrossPremium = transaction => {
	return transaction?.amount ? transaction?.amount : 0
}
const getNetPayable = transaction => {
	const policy = getPolicyFromTransactionObj(transaction)
	let grossPremium = getGrossPremium(transaction)
	return (
		grossPremium -
		getCommissionAmount("coverholder", transaction?.amount, policy)
	)
}
const getCurrencyTotal = (key, group) => {
	let output = []

	let type =
		key === "currency"
			? "Currency Total:"
			: key === "binder"
			? "Binder Grand Total"
			: null

	// group data by currencyCode
	let groupedData = {}
	group.map(transaction => {
		const currencyCode = transaction?.currencyCode
			? transaction?.currencyCode
			: ""
		if (!Object.keys(groupedData).includes(currencyCode)) {
			groupedData[currencyCode] = []
		}
		groupedData[currencyCode].push(transaction)
	})

	Object.values(groupedData)?.map((group, index) => {
		const totalInsuredAmount = group?.reduce((sum, transaction) => {
			const policy = getPolicyFromTransactionObj(transaction)
			const insuredAmount = getTotalInsuredAmount(policy)
			return sum + insuredAmount
		}, 0)
		const totalGrossPremium = group?.reduce((sum, transaction) => {
			const premium = getGrossPremium(transaction)
			return sum + premium
		}, 0)
		const totalAgencyCommissionAmount = group?.reduce((sum, transaction) => {
			const policy = getPolicyFromTransactionObj(transaction)
			const commissionAmount = getCommissionAmount(
				"coverholder",
				transaction?.amount,
				policy
			)
			return sum + commissionAmount
		}, 0)
		const totalNetPayable = group?.reduce((sum, policy) => {
			const netPayable = getNetPayable(policy)
			return sum + netPayable
		}, 0)
		output.push([
			"",
			"",
			"",
			"",
			"",
			"",
			"",
			"",
			"",
			"",
			"",
			"",
			"",
			"",
			index === 0 ? type : "",
			Object.keys(groupedData)?.[index],
			totalInsuredAmount,
			totalGrossPremium,
			"",
			totalAgencyCommissionAmount,
			totalNetPayable
		])
	})
	return output
}

/****************** Function for fetching ******************/
const fetchTransactions = async filterPayload => {
	const dateFrom = filterPayload?.issueDate?.after
		? dayjs(filterPayload?.issueDate?.after).format()
		: null
	const dateTo = filterPayload?.issueDate?.before
		? dayjs(filterPayload?.issueDate?.before)
				.add(1, "day")
				.format()
		: null

	const query = gql`
		fragment obj on policy {
			createdAt
			issueDate
			issuerNumber
			status
			startDate
			endDate
			premium {
				currencyCode
				originalPrice
			}
			stakeholders {
				entity {
					internalCode
				}
				type
			}
			contractHolder {
				name
				addresses {
					fields2 {
						type
						value
					}
				}
			}
			contractInsured {
				name
				addresses {
					fields2 {
						type
						value
					}
				}
			}
			facts {
				type
				value {
					stringValue
					numberValue
					dateValue
					booleanValue
				}
			}
			associatedContracts {
				id
				contract {
					startDate
					endDate
					bindingDate
					firstParty {
						name
					}
					secondParty {
						name
					}
					commissionRule {
						product {
							name
						}
						jsonRule
					}
					facts {
						type
						value {
							stringValue
							numberValue
							dateValue
						}
					}
				}
			}
			product {
				name
				insurer {
					name
				}
				productId {
					plan
					type
					version
				}
				benefits {
					name
				}
			}
			premium {
				originalPrice
			}
			transactions {
				id
				createdAt
				transactionNumber
			}
			valuesJsonString
			commissionSplitRules {
				type
				currencyCode
				amount
				remark
				ratio
			}
		}

		query fetchTransactions($transactionWhere: transactionWhereInput) {
			transactions(where: $transactionWhere) {
				list {
					amount
					dateTime
					status
					currencyCode
					object {
						__typename
						... on policy {
							...obj
							endorsements {
								beforeEndorsement {
									...obj
								}
							}
						}
						... on endorsement {
							afterEndorsement {
								...obj
							}
						}
					}
				}
			}
		}
	`
	const variables = {
		transactionWhere: {
			and: [
				{ status: "APPROVED" },
				{ dateTime_gt: dateFrom },
				{ dateTime_lt: dateTo }
			]
		}
	}

	const res = await api({ query, variables })
	//TODO: error handling
	return res?.data?.transactions?.list
}

/****************** Export ******************/
export default async filterPayload => {
	const fetchedData = await fetchTransactions(filterPayload)
	const workSheetData = [
		...header,
		...filterDates(filterPayload),
		...rows(fetchedData)
	]
	const workSheetColumns = subheader?.map(item => ({ wpx: 400 }))
	return { workSheetData, workSheetColumns }
}
